<!-- eslint-disable vue/no-multiple-template-root -->
<!-- eslint-disable vue/singleline-html-element-content-newline -->
<!-- eslint-disable vue/no-v-model-argument -->
<template>
  <!-- Element Upload 组件 -->
  <el-upload
    ref="upload"
    v-model="fileIds"
    v-model:file-list="fileList"
    :disabled="props.disabled"
    :list-type="props.listType"
    :multiple="props.multiple"
    :limit="props.limit"
    :headers="headers"
    class="upload-demo app-upload"
    :action="actionPath"
    :data="formData"
    :before-upload="beforeUpload"
    :on-success="handleSuccess"
    :on-preview="handlePreview"
    :on-remove="handleRemove"
    :before-remove="beforeRemove"
    :on-exceed="handleExceed"
  >
    <!-- 上传按钮区域 -->
    <div v-if="showButton">
      <el-button type="primary"> 上传 </el-button>
    </div>
    <el-icon v-else>
      <!-- 非文本模式显示加号图标 -->
      <Plus />
    </el-icon>

    <!-- 上传提示信息 -->
    <template #tip>
      <div class="el-upload__tip text-red">文件上传尺寸必须小于50MB</div>
    </template>

    <!-- 图片模式下的文件列表模板 -->
    <template v-if="props.listType !== 'text'" #file="{ file }">
      <div>
        <!-- 图片缩略图 -->
        <img
          class="el-upload-list__item-thumbnail"
          :src="fileUrl + file.url"
          alt="请上传图片"
        />
        <!-- 图片操作按钮 -->
        <span class="el-upload-list__item-actions">
          <!-- 预览按钮 -->
          <span
            class="el-upload-list__item-preview"
            @click="handlePreview(file)"
          >
            <el-icon><zoom-in /></el-icon>
          </span>
          <!-- 下载按钮 -->
          <span
            class="el-upload-list__item-delete"
            @click="handlePictureDownload(file)"
          >
            <el-icon><Download /></el-icon>
          </span>
          <!-- 删除按钮 -->
          <span
            class="el-upload-list__item-delete"
            @click="handlePictureRemove(file, fileList)"
          >
            <el-icon><Delete /></el-icon>
          </span>
        </span>
      </div>
    </template>
  </el-upload>

  <!-- 图片预览对话框 -->
  <el-dialog v-model="dialogVisible">
    <img
      w-full
      :src="dialogImageUrl"
      alt="Preview Image"
      class="bigHeadPortrait"
    />
  </el-dialog>
</template>

<script setup>
// Vue 相关引入
import { ref, reactive, toRaw, watch, getCurrentInstance } from 'vue';
// Element Plus 组件引入
import { ElMessage, ElMessageBox } from 'element-plus';
// Vuex 状态管理
import { useStore } from 'vuex';
// API 函数引入
import { downFileById } from '@/api/sys/fileInfo';

/**
 * 定义组件发出的事件
 * update:modelValue - 更新模型值
 * headPortraitData - 头像数据更新
 * fileData - 文件数据（新增，用于向量化处理）
 */
const emits = defineEmits([
    'update:modelValue',
    'headPortraitData',
    'fileData'
  ]),
  // 使用 Vuex store
  store = useStore(),
  // 文件URL前缀，从环境变量获取
  fileUrl = process.env.VUE_APP_FILE_API,
  // 获取当前组件实例
  { proxy } = getCurrentInstance(),
  // 父组件传入的属性定义
  props = defineProps({
    // 选中的文件ID值
    modelValue: {
      type: String,
      default: null
    },
    // 上传文件数量限制
    limit: {
      type: Number,
      default: 1
    },
    // 文件列表数组
    fileList: {
      type: Array,
      default: () => []
    },
    // 文件列表的类型："text" | "picture" | "picture-card"
    listType: {
      type: String,
      default: 'text'
    },
    // 是否支持多选文件
    multiple: {
      type: Boolean,
      default: false
    },
    // 是否禁用上传
    disabled: {
      type: Boolean,
      default: false
    },
    // 关联业务ID
    bizId: {
      type: String,
      default: null
    },
    // 业务类型
    bizType: {
      type: String,
      default: null
    }
  }),
  // 响应式变量定义
  // 文件列表
  fileList = ref([]),
  // 文件ID数组
  fileIds = ref([]),
  // 是否显示上传按钮
  showButton = ref(true),
  // 预览对话框是否可见
  dialogVisible = ref(false),
  // 预览对话框图片URL
  dialogImageUrl = ref(''),
  // 表单数据，用于上传时附加的数据
  formData = reactive({
    file: null, // 文件对象
    headPortrait: '' // 头像URL
  }),
  // 请求头，包含认证信息
  headers = ref({
    Authorization: store.getters.token
  }),
  // 上传服务器地址
  actionPath = ref(
    process.env.VUE_APP_BASE_API + 'auth-server/sys/fileInfo/upload'
  ),
  // 存储原始文件对象的Map，用于在上传成功后获取原始文件数据
  rawFileMap = new Map();

/**
 * 监听listType变化
 * 当listType变为'picture-card'时，隐藏上传按钮
 */
watch(
  () => props.listType,
  (newValue) => {
    if (newValue && newValue === 'picture-card') {
      showButton.value = false;
    }
  },
  { immediate: true, deep: true } // 立即执行，深度监听
);

/**
 * 监听父组件传入的fileList变化
 * 当fileList变化时，更新本地fileList和fileIds
 */
watch(
  () => props.fileList,
  (newValue) => {
    if (newValue && newValue.length > 0) {
      fileList.value = newValue;
      fileIds.value = newValue.map((element) => element.id);
      emits('update:modelValue', fileIds.value);
    } else {
      fileList.value = [];
      emits('update:modelValue', []);
    }
  },
  { immediate: true, deep: true } // 立即执行，深度监听
);

/**
 * 文件上传前的处理函数
 * @param {File} file - 文件对象
 * @returns {boolean} - 返回true允许上传，false阻止上传
 */
// eslint-disable-next-line newline-after-var, one-var
const beforeUpload = (file) => {
    // 设置业务ID和类型到formData
    if (props.bizId) {
      formData.bizId = props.bizId;
    }
    if (props.bizType) {
      formData.bizType = props.bizType;
    }
    formData.file = file;

    // 存储原始文件对象，用于后续传递给父组件
    rawFileMap.set(file.uid, file);

    // 允许的文件类型数组
    const type = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    // 文件类型验证
    if (type.indexOf(file.type) === -1) {
      ElMessage.error('上传的文件必须是jpeg jpg png doc docx xls xlsx之一');
      return false;
    } else if (file.size / 1024 / 1024 > 50) {
      // 文件大小验证（50MB限制）
      ElMessage.error('文件大小不能超过50MB');
      return false;
    }
    return true;
  },
  /**
   * 文件上传成功回调函数
   * @param {Object} response - 服务器响应
   * @param {Object} file - 文件对象
   * @param {Array} fileList - 文件列表
   */
  // eslint-disable-next-line no-shadow
  handleSuccess = (response, file, fileList) => {
    // 设置文件URL
    file.url = file.response.data.url;
    formData.headPortrait = file.response.data.url;
    // 发出头像数据事件
    emits('headPortraitData', file.response.data.url);

    // 获取原始文件对象并传递给父组件（用于向量化处理）
    const rawFile = rawFileMap.get(file.uid);

    if (rawFile) {
      // 发出文件数据事件，包含原始文件对象、文件信息和完整响应
      emits('fileData', {
        rawFile: rawFile, // 原始文件对象，可用于向量化处理
        fileInfo: file.response.data, // 服务器返回的文件信息
        response: response // 完整响应数据
      });
      // 清理缓存
      rawFileMap.delete(file.uid);
    }

    // 更新文件列表和文件IDs
    if (fileList && fileList.length > 0) {
      const rawList = toRaw(fileList);

      fileList.value = rawList;
      fileIds.value = rawList.map((element) => element.response.data.id);
      emits('update:modelValue', fileIds.value);
    }
  },
  /**
   * 文件列表移除文件时的钩子函数
   * @param {Object} file - 要移除的文件
   * @param {Array} uploadFiles - 上传文件列表
   */
  handleRemove = (file, uploadFiles) => {
    if (file) {
      const data = toRaw(file.response.data),
        // 从fileIds中移除对应的文件ID
        newFileIds = fileIds.value.filter((r) => r !== data.id);

      fileIds.value = newFileIds;
    }
  },
  /**
   * 文件下载函数
   * @param {string} id - 文件ID
   */
  download = (id) => {
    proxy.$apiLoding.queryLoding(id, downFileById, false);
  },
  /**
   * 点击文件列表中已上传的文件时的钩子函数
   * @param {Object} file - 点击的文件
   */
  handlePreview = (file) => {
    if (file) {
      if (props.listType === 'picture-card') {
        // 图片卡片模式：显示预览对话框
        dialogVisible.value = true;
        dialogImageUrl.value = fileUrl + file.url;
      } else {
        // 文本模式：直接下载文件
        const data = toRaw(file.response.data);

        download(data.id);
      }
    }
  },
  /**
   * 当超出文件数量限制时，执行的钩子函数
   * @param {Array} files - 超出的文件数组
   * @param {Array} uploadFiles - 上传文件列表
   */
  handleExceed = (files, uploadFiles) => {
    ElMessage.warning(
      `最多允许上传 ${props.limit} 个文件，本次选择了 ${files.length} 个文件`
    );
  },
  /**
   * 文件移除前的确认函数
   * @param {Object} file - 要移除的文件
   * @param {Array} uploadFiles - 上传文件列表
   * @returns {Promise} - 返回确认结果的Promise
   */
  beforeRemove = (file, uploadFiles) => {
    return ElMessageBox.confirm(`确定删除文件 ${file.name} 吗?`).then(
      () => true,
      () => false
    );
  },
  /**
   * 下载图片函数
   * @param {Object} file - 要下载的文件
   */
  handlePictureDownload = (file) => {
    if (file) {
      const data = toRaw(file);

      download(data.id);
    }
  },
  /**
   * 移除图片函数
   * @param {Object} file - 要移除的文件
   * @param {Array} list - 文件列表
   */
  handlePictureRemove = (file, list) => {
    if (file && list) {
      const dataList = toRaw(list),
        data = toRaw(file),
        // 从列表中移除文件
        newList = dataList.filter((element) => element.id !== data.id),
        // 从fileIds中移除对应的文件ID
        newFileIds = fileIds.value.filter((r) => r !== data.id);

      fileList.value = newList;
      fileIds.value = newFileIds;
    }
  };
</script>

<style lang="scss" scoped>
/* 上传组件样式 */
.app-upload {
  width: 500px !important;
}
/* 大头像预览样式 */
.bigHeadPortrait {
  width: 100%;
  height: 100%;
}
</style>
